/**
 * Filter state lives in the URL, not in a component.
 *
 * Three consequences, all of them the point: back and forward work because history *is*
 * the state; a filtered list is a link someone can paste into a message; and a saved view
 * is a stored query string with no parsed structure, so adding a filter parameter needs
 * no migration and no client release.
 *
 * Built on `useSearchParams` in declarative mode — this app has no data router, so
 * `loader`, `action` and `redirect()` do not exist here and are not used.
 */
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/** Never stored in a saved view: a view pinned to a stale cursor returns the wrong page
 *  forever, and a pinned page size is not a filter. */
const NOT_A_FILTER = ["limit", "cursor"];

export interface ListQuery {
  /** Every param, repeated keys preserved. */
  params: URLSearchParams;
  /** Single-valued read. */
  get(key: string): string | null;
  /** Multi-valued read, for `collection` and `tag`. */
  all(key: string): string[];
  /**
   * Merge a patch. `null` removes a key; an array replaces every value of it.
   * Any change except `cursor` itself resets `cursor` — a filter change with a stale
   * cursor returns a page from the middle of a different result set.
   */
  set(patch: Record<string, string | string[] | null>): void;
  /** Back to an empty query. */
  clear(): void;
  /**
   * Replaces every param at once, atomically. `set` can only ever merge onto whatever
   * `params` was at render time, so "clear everything, then set a view's params" cannot
   * be expressed as two `set`/`clear` calls in one handler — both read the same stale
   * `params` closure, and React keeps only the last `setParams` call, so the second call
   * merges onto the pre-clear state instead of an empty one. `replace` is the one caller
   * that needs this: applying a saved view.
   */
  replace(next: URLSearchParams): void;
  /** What a saved view stores: everything except `limit` and `cursor`. */
  toViewQuery(): string;
  /** What the fetch uses: everything. */
  toQueryString(): string;
}

export function useListQuery(): ListQuery {
  const [params, setParams] = useSearchParams();

  return useMemo<ListQuery>(() => {
    const set = (patch: Record<string, string | string[] | null>) => {
      const next = new URLSearchParams(params);
      for (const [key, value] of Object.entries(patch)) {
        next.delete(key);
        if (value === null) continue;
        for (const one of Array.isArray(value) ? value : [value]) next.append(key, one);
      }
      const keys = Object.keys(patch);
      if (!(keys.length === 1 && keys[0] === "cursor")) next.delete("cursor");
      setParams(next);
    };

    const withoutPagination = () => {
      const next = new URLSearchParams(params);
      for (const key of NOT_A_FILTER) next.delete(key);
      return next.toString();
    };

    return {
      params,
      get: (key) => params.get(key),
      all: (key) => params.getAll(key),
      set,
      clear: () => setParams(new URLSearchParams()),
      replace: (next) => setParams(next),
      toViewQuery: withoutPagination,
      toQueryString: () => params.toString(),
    };
  }, [params, setParams]);
}
